import { useEffect, useMemo, useRef, useState } from "react";
import { Pencil, Plus, Save, Trash2, ArrowLeft, ArrowRight, Printer, X, ChevronRight } from "lucide-react";
import { ProfileCard } from "../components/OrgCard";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { api, errMsg, fmtDate } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useApi, PageHeader, Loading, ErrorNote, Field, btn, inputCls } from "../components/kit";

const TIER = {
  leadership: ["President", "bg-[#4F46E5]", "ring-[#4F46E5]/25"],
  director: ["Executive", "bg-[#60A5FA]", "ring-[#60A5FA]/30"],
  lead: ["Managers & leads", "bg-[#4ADE80]", "ring-[#4ADE80]/30"],
  staff: ["Coordinators", "bg-[#F92838]", "ring-[#F92838]/25"],
  team: ["Teams", "bg-[#FBCFBB]", "ring-[#FBCFBB]/60"],
};
const RANK = { leadership: 0, director: 1, lead: 2, staff: 3, team: 4 };
const ROW_H = 192;
const uid = () => Math.random().toString(36).slice(2, 12);
const initials = (n) => n.split(/\s+/).filter((w) => /^[A-Za-z]/.test(w)).slice(0, 2).map((w) => w[0]).join("").toUpperCase();

function Card({ n, onClick, editing }) {
  const [, dot, ring] = TIER[n.tier] || TIER.staff;
  return (
    <button type="button" onClick={onClick} data-testid={`org-node-${n.id}`}
      className="group flex h-36 w-28 cursor-pointer flex-col items-center justify-start overflow-hidden pt-2 text-center">
      <span className={`flex h-14 w-14 items-center justify-center rounded-full ring-8 ${ring} ${dot} text-sm font-bold ${n.tier === "team" ? "text-[color:var(--ink)]" : "text-white"} transition-transform group-hover:scale-105`}>
        {n.tier === "team" ? "" : initials(n.name)}</span>
      <span className="mt-3 font-display text-[15px] font-semibold leading-tight">{n.name}</span>
      {n.title && <span className="mt-0.5 text-xs text-muted-foreground">{n.title}</span>}
      {editing && <span className="mt-1 inline-flex items-center gap-1 text-[11px] font-semibold text-[color:var(--dcbc-red)] opacity-0 group-hover:opacity-100"><Pencil className="h-3 w-3" /> Edit</span>}
    </button>
  );
}

function branch(n, kids, onPick, editing, row = n.row ?? RANK[n.tier] ?? 0) {
  const ch = (kids[n.id] || []).map((c) => [c, Math.max(c.row ?? RANK[c.tier] ?? row + 1, row + 1)]);
  return (
    <div key={n.id} className="flex flex-col items-center">
      <Card n={n} editing={editing} onClick={() => onPick(n)} />
      {ch.length > 0 && <span className="h-6 w-px bg-slate-300" />}
      {ch.length > 0 && <div className="flex">{ch.map(([c, r], i) => (
        <div key={c.id} className="relative flex flex-col items-center px-1.5" style={{ paddingTop: 24 + (r - row - 1) * ROW_H }}>
          {ch.length > 1 && <span className={`absolute top-0 h-px bg-slate-300 ${i === 0 ? "left-1/2 right-0" : i === ch.length - 1 ? "left-0 right-1/2" : "left-0 right-0"}`} />}
          <span className="absolute left-1/2 top-0 w-px bg-slate-300" style={{ height: 24 + (r - row - 1) * ROW_H }} />
          {branch(c, kids, onPick, editing, r)}
        </div>))}</div>}
    </div>
  );
}

function LevelList({ nodes, labels, onPick, editing }) {
  const byId = Object.fromEntries(nodes.map((n) => [n.id, n]));
  const levels = Object.keys(RANK).map((k) => [k, nodes.filter((n) => (TIER[n.tier] ? n.tier : "staff") === k)]).filter(([, ns]) => ns.length);
  return (
    <div className="space-y-6 lg:hidden print:hidden" data-testid="org-list">
      {levels.map(([k, ns]) => (
        <section key={k} data-testid={`org-level-${k}`}>
          <h2 className="mb-2 flex items-center gap-2 text-sm font-bold uppercase tracking-wide"><span className={`h-3 w-3 rounded-full ${TIER[k][1]}`} />{labels[k]}<span className="font-normal text-muted-foreground">({ns.length})</span></h2>
          <ul className="grid gap-2">{ns.map((n) => { const p = byId[n.parent_id]; return (
            <li key={n.id}><button type="button" onClick={() => onPick(n)} data-testid={`org-list-${n.id}`}
              className="flex w-full items-center gap-3 rounded-2xl border bg-white p-3 text-left transition-colors hover:border-[color:var(--ink)]">
              <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-xs font-bold ${TIER[k][1]} ${k === "team" ? "text-[color:var(--ink)]" : "text-white"}`}>{k === "team" ? "" : initials(n.name)}</span>
              <span className="min-w-0 flex-1"><span className="block font-semibold leading-tight">{n.name}</span>
                {n.title && <span className="block text-sm text-muted-foreground">{n.title}</span>}
                {p && <span className="mt-0.5 block text-xs text-muted-foreground">{k === "team" ? "Led by" : "Reports to"} <span className="font-semibold text-[color:var(--ink)]">{p.name}</span></span>}</span>
              {editing ? <Pencil className="h-4 w-4 shrink-0 text-muted-foreground" /> : <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />}
            </button></li>); })}</ul>
        </section>))}
    </div>
  );
}

function LabelsDialog({ labels, close, saved }) {
  const [f, setF] = useState(labels);
  const save = async () => { try { const r = await api.put("/org-chart/labels", { labels: f }); toast.success("Level names saved."); saved(r.data.labels); close(); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <Dialog open onOpenChange={(o) => !o && close()}>
      <DialogContent className="sm:max-w-md" data-testid="org-labels-dialog">
        <DialogHeader><DialogTitle>Rename levels</DialogTitle></DialogHeader>
        <div className="space-y-3">{Object.keys(RANK).map((k) => (
          <Field key={k} label={<span className="inline-flex items-center gap-2"><span className={`h-3 w-3 rounded-full ${TIER[k][1]}`} /> Colour {RANK[k] + 1}</span>} htmlFor={`ol-${k}`}>
            <input id={`ol-${k}`} className={inputCls} value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`org-label-${k}`} /></Field>))}
          <button className={`${btn.primary} w-full`} onClick={save} data-testid="org-labels-save">Save level names</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function below(id, nodes) {
  const out = new Set([id]);
  let grew = true;
  while (grew) { grew = false; nodes.forEach((n) => { if (n.parent_id && out.has(n.parent_id) && !out.has(n.id)) { out.add(n.id); grew = true; } }); }
  return out;
}

function NodeDialog({ node, nodes, users, setNodes, close, labels }) {
  const [f, setF] = useState(node);
  useEffect(() => setF(node), [node]);
  if (!node) return null;
  const blocked = below(node.id, nodes);
  const apply = (fn) => { setNodes(fn); close(); };
  const update = () => { if (!f.name.trim()) return toast.error("Give the box a name."); apply((ns) => ns.map((x) => (x.id === f.id ? { ...f } : x))); };
  const addChild = () => apply((ns) => [...ns.map((x) => (x.id === f.id ? { ...f } : x)), { id: uid(), parent_id: f.id, name: "New person", title: "", tier: "staff" }]);
  const remove = () => { if (!window.confirm(`Remove ${node.name}? Anyone reporting to them moves up a level.`)) return; apply((ns) => ns.filter((x) => x.id !== node.id).map((x) => (x.parent_id === node.id ? { ...x, parent_id: node.parent_id } : x))); };
  const shift = (d) => apply((ns) => {
    const sib = ns.filter((x) => x.parent_id === node.parent_id);
    const j = sib.findIndex((x) => x.id === node.id) + d;
    if (j < 0 || j >= sib.length) return ns;
    const a = ns.findIndex((x) => x.id === node.id), b = ns.findIndex((x) => x.id === sib[j].id);
    const s = [...ns]; [s[a], s[b]] = [s[b], s[a]]; return s;
  });
  return (
    <Dialog open onOpenChange={(o) => !o && close()}>
      <DialogContent className="sm:max-w-md" data-testid="org-node-dialog">
        <DialogHeader><DialogTitle>Edit box</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <Field label="Name" htmlFor="on"><input id="on" className={inputCls} value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="org-edit-name" /></Field>
          <Field label="Title" htmlFor="ot"><input id="ot" className={inputCls} value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} data-testid="org-edit-title" /></Field>
          <Field label="Portal account" htmlFor="ou" hint="Links the box to their profile so staff can see their email, department and roles.">
            <select id="ou" className={inputCls} value={f.user_id || ""} onChange={(e) => setF({ ...f, user_id: e.target.value || null })} data-testid="org-edit-user">
              <option value="">Not linked</option>{users.filter((u) => u.id === f.user_id || !nodes.some((x) => x.user_id === u.id)).map((u) => <option key={u.id} value={u.id}>{u.name} — {u.email}</option>)}</select></Field>
          <Field label="Colour group" htmlFor="og"><select id="og" className={inputCls} value={f.tier} onChange={(e) => setF({ ...f, tier: e.target.value })} data-testid="org-edit-tier">
            {Object.keys(TIER).map((k) => <option key={k} value={k}>{labels[k]}</option>)}</select></Field>
          <Field label="Row on the chart" htmlFor="orow" hint="Auto puts each colour on its own row. Pick a row to lengthen or shorten this box's line.">
            <select id="orow" className={inputCls} value={f.row ?? ""} onChange={(e) => setF({ ...f, row: e.target.value === "" ? null : Number(e.target.value) })} data-testid="org-edit-row">
              <option value="">Auto (by colour)</option>{[0, 1, 2, 3, 4, 5, 6, 7].map((k) => <option key={k} value={k}>Row {k + 1}</option>)}</select></Field>
          <Field label="Reports to" htmlFor="op"><select id="op" className={inputCls} value={f.parent_id || ""} onChange={(e) => setF({ ...f, parent_id: e.target.value || null })} data-testid="org-edit-parent">
            <option value="">Nobody (top of chart)</option>{nodes.filter((x) => !blocked.has(x.id)).map((x) => <option key={x.id} value={x.id}>{x.name}{x.title ? ` — ${x.title}` : ""}</option>)}</select></Field>
          <div className="flex flex-wrap gap-2">
            <button className={btn.primary} onClick={update} data-testid="org-edit-apply">Apply</button>
            <button className={btn.ghost} onClick={addChild} data-testid="org-edit-add-child"><Plus className="h-4 w-4" /> Add someone below</button>
            <button className={btn.ghost} aria-label="Move left" onClick={() => shift(-1)} data-testid="org-edit-left"><ArrowLeft className="h-4 w-4" /></button>
            <button className={btn.ghost} aria-label="Move right" onClick={() => shift(1)} data-testid="org-edit-right"><ArrowRight className="h-4 w-4" /></button>
            <button className={`${btn.ghost} text-rose-700`} onClick={remove} data-testid="org-edit-remove"><Trash2 className="h-4 w-4" /> Remove</button>
          </div>
          <p className="text-xs text-muted-foreground">Changes show on the chart right away. Press Save changes to publish them to everyone.</p>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function FitTree({ children }) {
  const outer = useRef(null);
  const inner = useRef(null);
  const [z, setZ] = useState(1);
  useEffect(() => {
    const fit = () => {
      if (!outer.current || !inner.current) return;
      const natural = inner.current.getBoundingClientRect().width / z;
      const next = Math.min(1, (outer.current.clientWidth - 48) / natural);
      if (Math.abs(next - z) > 0.01) setZ(next);
    };
    fit();
    const ro = new ResizeObserver(fit);
    ro.observe(outer.current);
    return () => ro.disconnect();
  });
  return (
    <div ref={outer} className="hidden overflow-x-auto rounded-2xl border bg-white p-6 lg:block print:block print:border-0" data-testid="org-tree">
      <div ref={inner} className="mx-auto flex w-max gap-10" style={{ zoom: z }}>{children}</div>
    </div>
  );
}

export default function OrgChart() {
  const { user } = useAuth();
  const { data, loading, error, reload } = useApi("/org-chart");
  const [nodes, setNodes] = useState(null);
  const [editing, setEditing] = useState(false);
  const [pick, setPick] = useState(null);
  const [view, setView] = useState(null);
  const [labelsOpen, setLabelsOpen] = useState(false);
  const [labels, setLabels] = useState(null);
  useEffect(() => { if (data?.labels) setLabels(data.labels); }, [data]);
  const users = useApi(user.is_superadmin ? "/users" : null);
  const onPick = (n) => (editing ? setPick(n) : setView(n.id));
  useEffect(() => { if (data) setNodes(data.nodes); }, [data]);
  const kids = useMemo(() => {
    const k = {};
    (nodes || []).forEach((n) => { (k[n.parent_id || "_root"] = k[n.parent_id || "_root"] || []).push(n); });
    return k;
  }, [nodes]);
  if (loading && !data) return <Loading />;
  if (error) return <ErrorNote error={error} />;
  if (!nodes || !labels) return <Loading />;
  const roots = kids._root || [];
  const save = async () => { try { await api.put("/org-chart", { nodes }); toast.success("Org chart published."); setEditing(false); reload(); } catch (e) { toast.error(errMsg(e)); } };
  const cancel = () => { setNodes(data.nodes); setEditing(false); };
  return (
    <div>
      <PageHeader eyebrow="Who's who" title="Org Chart" testid="org-header">
        <button className={`${btn.ghost} print:hidden`} onClick={() => window.print()} data-testid="org-print"><Printer className="h-4 w-4" /> Print</button>
        {user.is_superadmin && !editing && <button className={`${btn.dark} print:hidden`} onClick={() => setEditing(true)} data-testid="org-edit-toggle"><Pencil className="h-4 w-4" /> Edit chart</button>}
      </PageHeader>
      {editing && <div className="sticky top-20 z-20 mb-6 flex flex-wrap items-center gap-3 rounded-2xl border bg-white/95 p-4 shadow-sm backdrop-blur print:hidden" data-testid="org-edit-bar">
        <span className="text-sm">Tap any box to change its name, title, linked account, row or who it reports to.</span>
        <button className={`${btn.ghost} ml-auto`} onClick={() => setNodes([...nodes, { id: uid(), parent_id: null, name: "New person", title: "", tier: "staff" }])} data-testid="org-add-top"><Plus className="h-4 w-4" /> Add box</button>
        <button className={btn.ghost} onClick={() => setLabelsOpen(true)} data-testid="org-rename-levels"><Pencil className="h-4 w-4" /> Rename levels</button>
        <button className={btn.ghost} onClick={cancel} data-testid="org-cancel"><X className="h-4 w-4" /> Discard</button>
        <button className={btn.primary} onClick={save} data-testid="org-save"><Save className="h-4 w-4" /> Save changes</button>
      </div>}
      <FitTree>{roots.map((r) => branch(r, kids, onPick, editing))}</FitTree>
      <LevelList nodes={nodes} labels={labels} onPick={onPick} editing={editing} />
      <div className="mt-4 flex flex-wrap gap-4 text-xs text-muted-foreground" data-testid="org-legend">
        {Object.entries(TIER).map(([k, [, dot]]) => <span key={k} className="inline-flex items-center gap-1.5"><span className={`h-3 w-3 rounded-full ${dot}`} /> {labels[k]}</span>)}
        {data.updated_at && <span className="ml-auto">Updated {fmtDate(data.updated_at)}{data.updated_by ? ` by ${data.updated_by}` : ""}</span>}
      </div>
      {pick && <NodeDialog node={pick} nodes={nodes} users={users.data || []} setNodes={setNodes} close={() => setPick(null)} labels={labels} />}
      {labelsOpen && <LabelsDialog labels={labels} close={() => setLabelsOpen(false)} saved={setLabels} />}
      {view && <ProfileCard nid={view} onClose={() => setView(null)} onJump={setView} />}
    </div>
  );
}
